import { Box, Flex } from '@chakra-ui/react';
import {
  ChevronDown as ChevronDownIcon,
  Dropdown,
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownTrigger,
  Icon,
} from '@mesh.ai/rabbit';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';

import { AutoResizeInput } from 'uiToolkitV1/AutoResizeInput';
import { TextLabel } from 'uiToolkitV1/Typography';

import { API_KEYS_TAGS, REVIEW_ACTION_CATEGORIES_OPTIONS } from '../constant';

type ActionAgendaInputWithCategoryProps = {
  selectedCategory: keyof typeof REVIEW_ACTION_CATEGORIES_OPTIONS;
  onCategoryChange: (category: keyof typeof REVIEW_ACTION_CATEGORIES_OPTIONS) => void;
  dropdownRef: React.RefObject<HTMLDivElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  agendaText?: string;
  onChange?: (value: string) => void;
  onSubmit?: () => void;
  hideAgendaInput?: boolean;
};

export const ActionAgendaInputWithCategory = ({
  selectedCategory,
  onCategoryChange,
  dropdownRef,
  containerRef,
  agendaText,
  onChange,
  onSubmit,
  hideAgendaInput = false,
}: ActionAgendaInputWithCategoryProps) => {
  const { moduleName } = useOrganisationSettings();
  const tagDisplayLabel =
    selectedCategory === API_KEYS_TAGS.GOAL ? moduleName : REVIEW_ACTION_CATEGORIES_OPTIONS[selectedCategory]?.label;

  return (
    <Flex flexDir="column" w="full" p="0 12px" gap="8px" ref={containerRef}>
      {!hideAgendaInput && (
        <AutoResizeInput
          value={agendaText}
          onChange={(e) => {
            const inputValue = e.target.value.slice(0, 255);
            onChange(inputValue);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSubmit();
          }}
          placeholder="Enter the agenda points manually"
          _placeholder={{ fontWeight: '400', color: '#999999' }}
          style={{ fontWeight: '700' }}
        />
      )}
      <Dropdown>
        <Box
          sx={{
            'button[data-state="open"]': {
              borderBottom: '1px solid #0D819F',
            },
            'button[data-state="closed"]': {
              borderBottom: '1px dashed #999999',
            },
            'button[data-state="open"] .icon': {
              color: '#0D819F',
              transform: 'rotate(180deg)',
              transition: 'transform 0.2s',
            },
            "button[data-state='closed'] .icon": {
              color: '#333333',
            },
            'button[data-state="closed"]:hover': {
              borderBottom: '1px solid #4C4949',
            },
          }}
        >
          <DropdownTrigger>
            <Flex alignItems="center" minH="30px" h="auto" w="150px" maxW="400px" justifyContent="space-between">
              <Box
                borderRadius="4px"
                background="#DBF2D3"
                boxShadow="0px 0px 1px 0px rgba(0, 0, 0, 0.25)"
                display="flex"
                justifyContent="center"
                alignItems="flex-end"
                marginBottom="4px"
              >
                <TextLabel textAlign="center" color="#44812F" px="2px">
                  {tagDisplayLabel}
                </TextLabel>
              </Box>
              <Icon className="icon" IconSVG={ChevronDownIcon} />
            </Flex>
          </DropdownTrigger>
        </Box>
        <DropdownContent ref={dropdownRef}>
          <DropdownRadioGroup
            value={selectedCategory}
            onValueChange={(category) => onCategoryChange(category as keyof typeof REVIEW_ACTION_CATEGORIES_OPTIONS)}
          >
            {Object.entries(REVIEW_ACTION_CATEGORIES_OPTIONS).map(([reviewAction, category]) => {
              if (!category.hasAccess()) return null;

              return (
                <DropdownRadioItem key={reviewAction} value={reviewAction} style={{ gap: '8px' }}>
                  <Icon IconSVG={category.icon} />
                  {reviewAction === API_KEYS_TAGS.GOAL ? moduleName : category?.label}
                </DropdownRadioItem>
              );
            })}
          </DropdownRadioGroup>
        </DropdownContent>
      </Dropdown>
    </Flex>
  );
};
